import {
  ContentState,
  EditorState,
  Modifier,
  convertFromHTML,
  CompositeDecorator,
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import htmlToDraft from "html-to-draftjs";
import ReactDomServer from "react-dom/server";
import * as XLSX from "xlsx";

export const clearMask = (string) => {
  if (!string) return "";
  let result = string.toString();

  result = result.replace(/\D/g, "");

  return result;
};

export const hideEmail = (email) => {
  return email.replace(/(\w{3})[\w.-]+@([\w.]+\w)/, "$1***@$2");
};

export const sort = (array, selector = (item) => item, order = "ASC") => {
  let newArray;
  if (order === "ASC") {
    newArray = array.sort((a, b) => {
      if (selector(a) < selector(b)) {
        return -1;
      }
      if (selector(a) > selector(b)) {
        return 1;
      }
      return 0;
    });
  } else {
    newArray = array.sort((a, b) => {
      if (selector(a) > selector(b)) {
        return -1;
      }
      if (selector(a) < selector(b)) {
        return 1;
      }
      return 0;
    });
  }

  return newArray;
};

// Calculates progress for requests that fetch/upload large amounts of data
export const calculateProgress = (totalRecords, pageSize, currentPage) => {
  const totalRequests = Math.ceil(totalRecords / pageSize);
  const progress = Math.floor((100 / totalRequests) * currentPage);
  return progress >= 100 ? 99 : progress;
};

export const paginate = (arr, pageSize, currentPage) => {
  return arr.slice((currentPage - 1) * pageSize, currentPage * pageSize);
};

export const createPagesFromArray = (arr, pageSize) => {
  const totalPages = Math.ceil(arr.length / pageSize);
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push({
      number: i,
      data: paginate(arr, pageSize, i),
      progress: calculateProgress(arr.length, pageSize, i),
    });
  }
  return pages;
};

export const downloadAsXlsx = (data, file_name) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Planilha1");

  XLSX.writeFile(wb, `${file_name}.xlsx`);
};

export const transformEditorStateIntoData = (editorState) => {
  return stateToHTML(editorState.getCurrentContent());
};

export const addHTMLToEditor = (html, editorState) => {
  let { contentBlocks, entityMap } = htmlToDraft(html);
  let contentState = Modifier.replaceWithFragment(
    editorState.getCurrentContent(),
    editorState.getSelection(),
    ContentState.createFromBlockArray(contentBlocks, entityMap).getBlockMap(),
  );

  const result = EditorState.push(editorState, contentState, "insert-fragment");
  return result;
};

export const removeHTMLFromEditor = (html, editorState) => {
  return stateToHTML(editorState.getCurrentContent());
};

export const convertHtmlToText = (html) => {
  const blocksFromHtml = convertFromHTML(html);
  const state = ContentState.createFromBlockArray(
    blocksFromHtml.contentBlocks,
    blocksFromHtml.entityMap,
  );
  return state;
};

export const convertHtmlToString = (html) => {
  return ReactDomServer.renderToString(html);
};

export const parseLargeStringIntoReticences = (string, maxLength = 35) => {
  if (string.length > maxLength) {
    const slicedName = string.slice(0, maxLength);
    return `${slicedName}...`;
  }

  return string;
};

export const findLinkEntities = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === "LINK"
    );
  }, callback);
};

export const htmlToText = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText;
};

export const appUrl =
  // eslint-disable-next-line no-undef
  !process.env.NODE_ENV || process.env.NODE_ENV === "development"
    ? "http://localhost:3000"
    : `https://${window.location.host}/api`;

export const poolTypeConversion = (type) => {
  const options = { CLOSED: "Fechado", OPEN: "Aberto" };

  return options[type];
};

export const poolTypeOptions = [
  { value: "OPEN", label: "Aberto" },
  { value: "CLOSED", label: "Fechado" },
];

export const isValidPositiveInteger = (str) => {
  if (str == null) return false;
  const num = Number(str);
  if (Number.isNaN(num) || !Number.isInteger(num) || num <= 0) return false;
  return true;
};

export const isValidPositiveNumber = (str, allow_zero_equality) => {
  if (str == null) return false;
  const num = Number(str);
  if (allow_zero_equality) {
    if (Number.isNaN(num) || num < 0) return false;
  } else {
    if (Number.isNaN(num) || num <= 0) return false;
  }
  return true;
};

export const convertToInternationalCurrencySystem = (value) => {
  return Math.abs(Number(value)) >= 1.0e9
    ? (Math.abs(Number(value)) / 1.0e9).toFixed(0) + "B"
    : Math.abs(Number(value)) >= 1.0e6
      ? (Math.abs(Number(value)) / 1.0e6).toFixed(0) + "M"
      : Math.abs(Number(value)) >= 1.0e3
        ? (Math.abs(Number(value)) / 1.0e3).toFixed(0) + "K"
        : Math.abs(Number(value));
};

export const simplifyNumberByUnitType = (value, unitType) => {
  switch (unitType) {
    case "MONEY":
      return convertToInternationalCurrencySystem(value);
    case "PERCENTAGE":
      return `${Math.floor(value)}%`;
    case "NUMBER":
      return value;

    default:
      return value;
  }
};

export const monthNumbers = {
  1: "Jan",
  2: "Fev",
  3: "Mar",
  4: "Abr",
  5: "Mai",
  6: "Jun",
  7: "Jul",
  8: "Ago",
  9: "Set",
  10: "Out",
  11: "Nov",
  12: "Dez",
};

export const formatUpperBoundForDisplay = (upperBound, unitType) => {
  if (
    !upperBound ||
    unitType !== "NUMBER" ||
    !Number.isInteger(Number(upperBound))
  )
    return upperBound;
  return Number(upperBound) - 1;
};

export const scrollToTopOfThePage = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

export const Link = (props, theme) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a
      href={url}
      target="_blank"
      style={{
        color: theme.colors.feedbackState.darked,
        textDecoration: "underline",
      }}
      rel="noreferrer"
    >
      {props.children}
    </a>
  );
};

export const createEditorStateWithDecorator = (content, theme) => {
  const decorator = new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: (props) => Link(props, theme),
    },
  ]);

  return EditorState.createWithContent(content, decorator);
};
